import { FormEvent, useState } from 'react';

import { urls } from '@zapier/identity-utils';
import { fetch } from '../../../utils';

import { parseHash } from './parseHash';

type Input = {
  hash: string;
};

type ResetPasswordStatus = 'initial' | 'submitting' | 'success';

type ResetPasswordData = {
  password1: string;
  password2: string;
};

type ResetPasswordResult = {
  errors?: string[];
};

async function submitResetPassword(data: ResetPasswordData, hash: string) {
  const { code, username } = parseHash(hash);

  const resp = await fetch<ResetPasswordResult>(urls.RESET_PASSWORD_API_URL, {
    method: 'POST',
    body: {
      ...data,
      code,
      username,
    },
  });

  if (resp.body.errors) {
    return resp.body.errors[0];
  }

  if (resp.status === 200) {
    // Yay! Success
    return '';
  } else if (resp.status === 400 || resp.status == 401 || resp.status === 403) {
    return 'Reset was invalid.';
  } else if (resp.status == 429) {
    return 'You’re trying too frequently.';
  }

  // Unknown error
  return 'Yikes! Something went wrong. If you keep having trouble, please contact support.';
}

export function useResetPassword({ hash }: Input) {
  const [formData, setFormData] = useState<ResetPasswordData>({
    password1: '',
    password2: '',
  });
  const [error, setError] = useState<string | undefined>();
  const [status, setStatus] = useState<ResetPasswordStatus>('initial');

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    setStatus('submitting');
    setError(undefined);

    const error = await submitResetPassword(formData, hash);

    setError(error);
    setStatus(error ? 'initial' : 'success');
  }

  function handleChange(field: string, value: string) {
    setFormData(data => ({
      ...data,
      [field]: value,
    }));
  }

  return {
    canSubmit:
      formData.password1.length > 0 &&
      formData.password2.length > 0 &&
      status !== 'submitting',
    error,
    formData,
    handleChange,
    handleSubmit,
    status,
  };
}
